import React, { Component } from "react";
import api from "../utils/api";
import { toast } from "react-toastify";

class AdminManageSpendPage extends Component {
  state = {
    sources: [],
    campaigns: [],
    spendData: [],
    budgetData: [],

    currentDate: new Date(),
    dateRange: [], // Array of date strings YYYY-MM-DD
    
    loading: false,

    // Modal state
    showModal: false,
    selectedCell: null,
    modalCampaigns: [],
  };

  componentDidMount() {
    this.generateDateRange(new Date());
  }

  generateDateRange = (baseDate) => {
    // Generate dates for the month of baseDate
    const year = baseDate.getFullYear();
    const month = baseDate.getMonth();
    const numDays = new Date(year, month + 1, 0).getDate();
    
    const range = [];
    for (let i = 1; i <= numDays; i++) {
      const d = new Date(year, month, i);
      const str = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      range.push(str);
    }
    
    this.setState({ currentDate: baseDate, dateRange: range }, this.fetchData);
  };

  prevMonth = () => {
    const prev = new Date(this.state.currentDate);
    prev.setMonth(prev.getMonth() - 1);
    this.generateDateRange(prev);
  };

  nextMonth = () => {
    const next = new Date(this.state.currentDate);
    next.setMonth(next.getMonth() + 1);
    this.generateDateRange(next);
  };

  fetchData = async () => {
    const { dateRange } = this.state;
    if (!dateRange.length) return;
    
    this.setState({ loading: true });
    try {
      const start_date = dateRange[0];
      const end_date = dateRange[dateRange.length - 1];
      const res = await api.get(`/admin/marketing/spend?start_date=${start_date}&end_date=${end_date}`);
      
      if (res.data.success) {
        const sortedCampaigns = (res.data.data.campaigns || []).sort((a, b) => 
          (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: 'base' })
        );
        this.setState({
          sources: res.data.data.sources,
          campaigns: sortedCampaigns,
          spendData: res.data.data.spendData,
          budgetData: res.data.data.budgetData || [],
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error fetching spend data");
    } finally {
      this.setState({ loading: false });
    }
  };

  openCellModal = (source, dateStr) => {
    const { campaigns, spendData, budgetData } = this.state;
    const sourceCampaigns = campaigns.filter(c => c.source_id === source.id);

    const modalCampaigns = sourceCampaigns.map(c => {
      const existingSpend = spendData.find(s => {
        const d = new Date(s.entry_date);
        const entryDateLocal = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        return s.campaign_id === c.id && entryDateLocal === dateStr;
      });

      // A budget change recorded exactly on this day - editing it here edits
      // that change in place. Otherwise the field stays blank: this day only
      // inherits an earlier change, and saving spend alone must not stamp a
      // new one. The inherited value is shown separately, as a hint only.
      const explicitBudget = budgetData.find(b => {
        const d = new Date(b.effective_date);
        const effectiveDateLocal = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        return b.campaign_id === c.id && effectiveDateLocal === dateStr;
      });

      return {
        ...c,
        budget: explicitBudget ? explicitBudget.budget : "",
        effectiveBudgetHint: !explicitBudget && existingSpend ? existingSpend.budget : null,
        spent: existingSpend ? existingSpend.spent : "",
      };
    });

    this.setState({
      showModal: true,
      selectedCell: { source, date: dateStr },
      modalCampaigns,
    });
  };

  closeModal = () => this.setState({ showModal: false });

  handleModalInputChange = (index, field, value) => {
    const updated = [...this.state.modalCampaigns];
    updated[index][field] = value;
    this.setState({ modalCampaigns: updated });
  };

  saveSpendData = async (e) => {
    e.preventDefault();
    const { selectedCell, modalCampaigns } = this.state;
    
    try {
      const hasValue = (v) => v !== "" && v != null;
      const payload = modalCampaigns
        .filter(c => hasValue(c.budget) || hasValue(c.spent))
        .map(c => ({
          source_id: selectedCell.source.id,
          campaign_id: c.id,
          entry_date: selectedCell.date,
          // Omitted (not 0) when left blank, so the backend leaves that
          // field untouched instead of stamping a 0 budget/spent change.
          ...(hasValue(c.budget) ? { budget: parseFloat(c.budget) } : {}),
          ...(hasValue(c.spent) ? { spent: parseFloat(c.spent) } : {}),
        }));
      
      await api.post("/admin/marketing/spend", { data: payload });
      
      toast.success("Saved successfully");
      this.closeModal();
      this.fetchData();
    } catch (err) {
      toast.error("Error saving data");
    }
  };

  getCellData = (sourceId, dateStr) => {
    const { campaigns, spendData } = this.state;
    const sourceCampaigns = campaigns.filter(c => c.source_id === sourceId).map(c => c.id);
    
    let totalBudget = 0;
    let totalSpent = 0;
    let hasData = false;
    
    spendData.forEach(s => {
      const d = new Date(s.entry_date);
      const entryDateLocal = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (entryDateLocal === dateStr && sourceCampaigns.includes(s.campaign_id)) {
        totalBudget += parseFloat(s.budget || 0);
        totalSpent += parseFloat(s.spent || 0);
        hasData = true;
      }
    });

    if (!hasData) return "-";
    return (
      <div style={{ lineHeight: "1.5" }}>
        <div className="adm-cell-budget">₹{totalBudget.toFixed(0)}</div>
        <div className="adm-cell-spent">₹{totalSpent.toFixed(0)}</div>
      </div>
    );
  };

  getDayTotalData = (dateStr) => {
    const { spendData } = this.state;
    let totalBudget = 0;
    let totalSpent = 0;
    let hasData = false;
    
    spendData.forEach(s => {
      const d = new Date(s.entry_date);
      const entryDateLocal = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (entryDateLocal === dateStr) {
        totalBudget += parseFloat(s.budget || 0);
        totalSpent += parseFloat(s.spent || 0);
        hasData = true;
      }
    });

    if (!hasData) return "-";
    return (
      <div style={{ fontSize: "13px", lineHeight: "1.3", fontWeight: "bold" }}>
        <div>B: {totalBudget.toFixed(2)}</div>
        <div>S: {totalSpent.toFixed(2)}</div>
      </div>
    );
  };

render() {
  const {
    sources,
    dateRange,
    currentDate,
    loading,
    showModal
  } = this.state;

  const monthYear = currentDate.toLocaleString("default", {
    month: "long",
    year: "numeric"
  });

  const totalEntries = this.state.spendData.length;
  const activeSources = sources.length;
  const monthTotals = dateRange.reduce((acc, d) => {
    this.state.spendData.forEach(s => {
      const sd = new Date(s.entry_date);
      const local = `${sd.getFullYear()}-${String(sd.getMonth()+1).padStart(2,'0')}-${String(sd.getDate()).padStart(2,'0')}`;
      if (local === d) {
        acc.budget += parseFloat(s.budget || 0);
        acc.spent += parseFloat(s.spent || 0);
      }
    });
    return acc;
  }, { budget: 0, spent: 0 });

  return (
    <div className="adm-page">
      <div className="adm-page-inner">
        <div className="adm-header">
          <div>
            <h4 className="adm-header-title">
              <span className="adm-icon-badge"><i className="fas fa-chart-bar"></i></span>
              Manage Spend / Budget
            </h4>
            <p className="adm-header-sub">
              Click any cell to log spend for that date, or set a new budget effective from that date onward (it carries forward until the next change).
            </p>
          </div>
          <div className="adm-toolbar">
            <span className="adm-badge adm-badge-primary"><i className="fas fa-globe"></i>{activeSources} sources</span>
            <span className="adm-badge adm-badge-muted"><i className="fas fa-list"></i>{totalEntries} entries</span>
          </div>
        </div>

        <div className="adm-kpi-row" style={{ marginBottom: "20px" }}>
          <div className="adm-kpi">
            <div className="adm-kpi-val" style={{ color: "var(--adm-primary)" }}>₹ {monthTotals.budget.toFixed(2)}</div>
            <div className="adm-kpi-lbl">Budget this month</div>
          </div>
          <div className="adm-kpi">
            <div className="adm-kpi-val" style={{ color: "var(--adm-danger)" }}>₹ {monthTotals.spent.toFixed(2)}</div>
            <div className="adm-kpi-lbl">Spent this month</div>
          </div>
          <div className="adm-kpi">
            <div className="adm-kpi-val">{activeSources}</div>
            <div className="adm-kpi-lbl">Active sources</div>
          </div>
        </div>

        <div className="adm-card">
          <div className="adm-card-body">
            <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: "16px" }}>
              <button className="adm-btn adm-btn-outline adm-btn-sm" onClick={this.prevMonth}>
                <i className="fas fa-chevron-left"></i>Prev Month
              </button>
              <h5 style={{ margin: "0 10px", fontWeight: 700, color: "var(--adm-text)", display: "inline-block" }}>{monthYear}</h5>
              <button className="adm-btn adm-btn-outline adm-btn-sm" onClick={this.nextMonth}>
                Next Month<i className="fas fa-chevron-right"></i>
              </button>

            <p className="text-muted small" style={{ marginLeft: 20, display: "inline-block"  }}>
              Each cell: <strong style={{ color: "var(--adm-primary)" }}>Budget</strong> / <strong style={{ color: "var(--adm-danger)" }}>Spent</strong>
            </p>
            </div>


            {loading ? (
              <div className="adm-spinner-row"><i className="fas fa-spinner fa-spin"></i></div>
            ) : (
              <div className="adm-matrix-wrap" style={{ maxHeight: "calc(100vh - 360px)", minHeight: "200px" }}>
                <table className="adm-matrix-table">
                  <thead>
                    <tr>
                      <th className="adm-matrix-pin">Source / Date</th>
                      {dateRange.map(d => (
                        <th key={d} style={{ minWidth: "68px" }}>{d.substring(8, 10)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sources.map(s => (
                      <tr key={s.id}>
                        <td className="adm-matrix-pin">{s.title}</td>
                        {dateRange.map(d => {
                          const cellData = this.getCellData(s.id, d);
                          return (
                            <td key={d} className="adm-matrix-cell" onClick={() => this.openCellModal(s, d)}>
                              {cellData === "-" ? (
                                <span className="adm-cell-muted">–</span>
                              ) : cellData}
                            </td>
                          );
                        })}
                      </tr>
                    ))}

                    <tr className="adm-matrix-total">
                      <td className="adm-matrix-pin">Total</td>
                      {dateRange.map(d => {
                        const totalData = this.getDayTotalData(d);
                        return (
                          <td key={`total-${d}`}>
                            {totalData === "-" ? (
                              <span className="adm-cell-muted">–</span>
                            ) : totalData}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="adm-modal-overlay">
          <div className="adm-modal-panel wide">
            <div className="adm-modal-header">
              <h5>
                <i className="fas fa-pencil-alt" style={{ color: "var(--adm-primary)" }}></i>
                Edit Spend &amp; Budget
              </h5>
              <button type="button" className="adm-close-btn" onClick={this.closeModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={this.saveSpendData}>
              <div className="adm-modal-body">
                <div className="adm-kpi-row" style={{ marginBottom: "16px" }}>
                  <div className="adm-kpi" style={{ flex: "1 1 auto" }}>
                    <div className="adm-kpi-val" style={{ fontSize: "14px" }}>{this.state.selectedCell?.source?.title}</div>
                    <div className="adm-kpi-lbl">Source</div>
                  </div>
                  <div className="adm-kpi" style={{ flex: "1 1 auto" }}>
                    <div className="adm-kpi-val" style={{ fontSize: "14px" }}>{this.state.selectedCell?.date}</div>
                    <div className="adm-kpi-lbl">Date</div>
                  </div>
                </div>

                <p className="text-muted small" style={{ marginBottom: "16px" }}>
                  <i className="fas fa-info-circle mr-1"></i>
                  Budget takes effect from this date onward, until the next budget change. Spent applies only to this date.
                </p>

                {this.state.modalCampaigns.length === 0 ? (
                  <div className="adm-empty">
                    <i className="fas fa-folder-open"></i>
                    <strong>No campaigns</strong>
                    <span>No active campaigns found for this source.</span>
                  </div>
                ) : (
                  <div className="adm-table-wrap">
                    <table className="adm-table">
                      <thead>
                        <tr>
                          <th>Campaign</th>
                          <th>New Budget (from this date)</th>
                          <th>Spent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {this.state.modalCampaigns.map((camp, idx) => (
                          <tr key={camp.id}>
                            <td style={{ fontWeight: 700, minWidth: "140px" }}>{camp.name}</td>
                            <td style={{ minWidth: "200px" }}>
                              <input
                                type="number" step="0.01"
                                style={{ width: "100%", height: "34px", borderRadius: "8px", border: "1px solid var(--adm-border)", padding: "4px 10px", background: "#f8fafc" }}
                                placeholder={
                                  camp.effectiveBudgetHint != null
                                    ? `Currently ${parseFloat(camp.effectiveBudgetHint).toFixed(2)}`
                                    : "No budget set yet"
                                }
                                value={camp.budget}
                                onChange={e => this.handleModalInputChange(idx, "budget", e.target.value)}
                              />
                              {camp.effectiveBudgetHint != null && (
                                <small className="text-muted" style={{ display: "block", marginTop: "4px" }}>
                                  Carried forward. Leave blank to keep it.
                                </small>
                              )}
                            </td>
                            <td style={{ minWidth: "140px" }}>
                              <input
                                type="number" step="0.01"
                                style={{ width: "100%", height: "34px", borderRadius: "8px", border: "1px solid var(--adm-border)", padding: "4px 10px", background: "#f8fafc" }}
                                value={camp.spent}
                                onChange={e => this.handleModalInputChange(idx, "spent", e.target.value)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="adm-modal-footer">
                <button type="button" className="adm-btn adm-btn-outline" onClick={this.closeModal}>Cancel</button>
                <button type="submit" className="adm-btn adm-btn-primary" disabled={this.state.modalCampaigns.length === 0}>
                  <i className="fas fa-save"></i>Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
}

export default AdminManageSpendPage;
